import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDeleteInspector, useInspector } from '../../api/inspectors';
import { useIsAdmin } from '../../api/me';
import { isApiError } from '../../api/errors';
import { Breadcrumbs, Button, ButtonGroup, ButtonLink, ConfirmDialog, ErrorSummary, SummaryList, Tabs } from '../../components/idsk';
import { useNavigateWithToast } from '../../components/common/navigateWithToast';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { formatDateTime } from '../../lib/format';
import { formatPhone, maskRodneCislo } from '../../validation/rules';
import { QueryError } from '../common/QueryError';
import { LoadingPage } from '../common/LoadingPage';
import { RequireAdmin } from '../common/RequireAdmin';
import { EntityHistory } from '../history/EntityHistory';

export function InspectorDetailPage() {
  return (
    <RequireAdmin>
      <InspectorDetail />
    </RequireAdmin>
  );
}

function InspectorDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const admin = useIsAdmin();
  const query = useInspector(id);
  const remove = useDeleteInspector();
  const navigateWithToast = useNavigateWithToast();
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  usePageTitle([query.data?.displayName ?? t('common.loading'), t('inspectors.title')]);
  if (query.error) return <QueryError error={query.error} asPage />;
  if (query.isPending) return <LoadingPage />;
  const i = query.data;

  const onDelete = async () => {
    setConfirming(false);
    try {
      await remove.mutateAsync({ id: i.id, version: i.version });
      navigateWithToast('/inspektori', t('inspectors.deleted', { name: i.displayName }));
    } catch (e) {
      setDeleteError(isApiError(e) && e.isVersionConflict ? t('errors.versionConflict') : isApiError(e) ? e.message : t('errors.unexpected'));
    }
  };

  const details = (
    <>
      <h2 className="govuk-heading-m">{t('inspectors.detailsHeading')}</h2>
      <SummaryList
        rows={[
          ...(admin ? [{ key: t('common.id'), value: <span className="app-mono">{i.id}</span> }] : []),
          { key: t('inspectors.fields.titleBefore'), value: i.titleBefore || t('common.notSet') },
          { key: t('inspectors.fields.firstName'), value: i.firstName },
          { key: t('inspectors.fields.lastName'), value: i.lastName },
          { key: t('inspectors.fields.titleAfter'), value: i.titleAfter || t('common.notSet') },
          { key: t('inspectors.fields.personalNumber'), value: admin && !i.personalNumberMasked ? i.personalNumber : maskRodneCislo(i.personalNumber) },
          { key: t('inspectors.fields.phone'), value: formatPhone(i.phone) },
          { key: t('inspectors.fields.email'), value: i.email },
          { key: t('inspectors.fields.account'), value: i.linkedToUser ? t('inspectors.linked') : t('inspectors.notLinked') },
          { key: t('common.updatedAt'), value: `${formatDateTime(i.lastModifiedAt)} (${i.lastModifiedBy})` },
        ]}
      />
    </>
  );

  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('inspectors.title'), to: '/inspektori' }, { text: i.displayName }]} />
      {deleteError && <ErrorSummary title={t('inspectors.deleteFailed')} errors={[{ text: deleteError }]} />}
      <PageHeading>{i.displayName}</PageHeading>
      <ButtonGroup>
        <ButtonLink to={`/inspektori/${i.id}/upravit`} variant="secondary">
          {t('common.edit')}
        </ButtonLink>
        <Button variant="warning" onClick={() => setConfirming(true)}>
          {t('common.delete')}
        </Button>
      </ButtonGroup>
      <Tabs
        title={t('common.contents')}
        items={[
          { id: 'udaje', label: t('subjects.tabs.details'), panel: details },
          { id: 'historia', label: t('history.tab'), panel: <EntityHistory entity="inspectors" id={i.id} /> },
        ]}
      />
      <ConfirmDialog
        open={confirming}
        title={t('inspectors.confirmDelete.title')}
        confirmText={t('inspectors.confirmDelete.confirm')}
        danger
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirming(false)}
      >
        <p className="govuk-body">{t('inspectors.confirmDelete.body', { name: i.displayName })}</p>
      </ConfirmDialog>
    </>
  );
}
